import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { API_ENDPOINTS, apiCall } from '../config/api';
import { getWeatherInfo, isWeatherAvailable, isHourlyWeatherAvailable } from '../utils/weatherUtils';
import { weatherCache } from '../utils/weatherCache';
import logger from '../utils/logger';
import WorkoutBadge from './WorkoutBadge';
import '../styles/DayTimeSlot.css';

/**
 * DayTimeSlot component displays a time slot (morning/afternoon/evening) 
 * with weather information and workouts for a specific day
 */
function DayTimeSlot({
  dayObj,
  timeSlot,
  workouts = [],
  triClubEvents = [],
  draggedWorkout,
  dragOverDate,
  dragOverTimeSlot,
  onDragOver,
  onDragLeave,
  onDrop,
  onWorkoutDragStart,
  onWorkoutDragEnd,
  onWorkoutClick,
  onWorkoutSelectionToggle,
  onWorkoutHeatTrainingChange,
  onAddWorkout,
  getTimeOfDayLabel
}) {
  // Convert date to stable string for dependency tracking
  const dateStr = dayObj.date.toISOString().split('T')[0]; // YYYY-MM-DD format

  // Determine forecast availability and check cache at render time so we can
  // initialize state without needing synchronous setState in the effect.
  const isValidTimeSlot = timeSlot === 'morning' || timeSlot === 'afternoon' || timeSlot === 'evening';
  const weatherAvailable = isValidTimeSlot && isWeatherAvailable(dateStr);
  const hasHourlyForecast = weatherAvailable && isHourlyWeatherAvailable(dateStr);
  const cachedWeather = weatherAvailable ? weatherCache.get(dateStr, timeSlot) : null;
  // For daily forecasts (days 8-16), only morning slot shows weather
  const shouldSkipSlot = weatherAvailable && !hasHourlyForecast && timeSlot !== 'morning';

  // When a fetch will be needed (no cache, valid slot, weather available), start in loading state
  const needsFetch = weatherAvailable && !cachedWeather && !shouldSkipSlot;

  const [weather, setWeather] = useState(cachedWeather);
  const [isDailyForecast, setIsDailyForecast] = useState(weatherAvailable ? !hasHourlyForecast : false);
  const [loading, setLoading] = useState(needsFetch);
  const [error, setError] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Periodically check if weather data is stale and trigger refresh
  useEffect(() => {
    if (timeSlot !== 'morning' && timeSlot !== 'afternoon' && timeSlot !== 'evening') {
      return;
    }
    
    if (!isWeatherAvailable(dateStr)) {
      return;
    }

    // Check every 5 minutes if the weather data is stale
    const staleCheckInterval = setInterval(() => {
      if (weatherCache.isStale(dateStr, timeSlot)) {
        // Trigger a refresh by updating the refreshTrigger state
        setRefreshTrigger(prev => prev + 1);
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(staleCheckInterval);
  }, [dateStr, timeSlot]);

  // Fetch weather - use hourly (time-of-day) for days 0-7, daily for days 8-16.
  // Cache hits, availability checks, and daily-forecast slot skipping are handled
  // at render time (above) to avoid synchronous setState in the effect.
  useEffect(() => {
    // Skip if not a valid time slot, weather unavailable, already cached, or wrong slot for daily
    if (!weatherAvailable || cachedWeather || shouldSkipSlot) {
      return;
    }

    // loading is already true from useState initialization (needsFetch).
    // Track if component is still mounted
    let isMounted = true;
    const hasHourlyForecast = isHourlyWeatherAvailable(dateStr);

    const fetchWeather = async () => {
      try {
        let weatherData;

        if (hasHourlyForecast) {
          // Deduplicate concurrent requests for the same date (thundering herd).
          // When morning/afternoon/evening mount simultaneously, only the first
          // issues a network call; the others await the same in-flight promise.
          let fetchPromise = weatherCache.getInFlight(dateStr);
          if (!fetchPromise) {
            fetchPromise = weatherCache.setInFlight(dateStr, (async () => {
              const url = API_ENDPOINTS.WEATHER_BY_TIME_OF_DAY(dateStr);
              const response = await apiCall(url);
              if (!response.ok) return null;
              const data = await response.json();
              if (data.morning) weatherCache.set(dateStr, 'morning', data.morning);
              if (data.afternoon) weatherCache.set(dateStr, 'afternoon', data.afternoon);
              if (data.evening) weatherCache.set(dateStr, 'evening', data.evening);
              return data;
            })());
          }

          const data = await fetchPromise;
          if (!data) {
            if (isMounted) {
              setError(true);
              setWeather(null);
              setIsDailyForecast(false);
            }
            return;
          }
          weatherData = data[timeSlot];
        } else {
          // Use daily endpoint for days 8-16
          const url = API_ENDPOINTS.WEATHER_BY_DATE(dateStr);
          const response = await apiCall(url);
          
          if (!response.ok) {
            if (isMounted) {
              setError(true);
              setWeather(null);
            }
            return;
          }
          
          weatherData = await response.json();
        }
        
        // Only update state if component is still mounted
        if (isMounted) {
          // Cache the weather data
          weatherCache.set(dateStr, timeSlot, weatherData);
          
          setWeather(weatherData);
          setError(false);
        }
      } catch (err) {
        logger.error('Error fetching weather:', err);
        if (isMounted) {
          setError(true);
          setWeather(null);
          setIsDailyForecast(false);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchWeather();

    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [dateStr, timeSlot, refreshTrigger, weatherAvailable, cachedWeather, shouldSkipSlot]);

  const isBeingDraggedOver = draggedWorkout &&
    dragOverDate === dayObj.date.toISOString() &&
    dragOverTimeSlot === timeSlot;

  const hasContent = workouts.length > 0;

  const isScheduledSlot = timeSlot === 'morning' || timeSlot === 'afternoon' || timeSlot === 'evening';
  // Only show weather for scheduled time slots, not unscheduled
  const shouldShowWeather = isScheduledSlot && weather;
  const weatherInfo = shouldShowWeather ? getWeatherInfo(weather.weather_code) : null;
  // Show error indicator only when fetch was attempted (date in range) but failed
  const shouldShowWeatherError = isScheduledSlot && error;

  return (
    <div
      className={`time-slot ${timeSlot} ${isBeingDraggedOver ? 'drag-over' : ''}`}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {/* Header with time label, add button, and weather */}
      <div className="time-slot-header-row">
        <div className="time-slot-label-row">
          <div className="time-slot-header">
            {getTimeOfDayLabel(timeSlot)}
            {isDailyForecast && shouldShowWeather && (
              <span className="daily-forecast-indicator" title="Full-day forecast">📅</span>
            )}
          </div>
          {isScheduledSlot && onAddWorkout && (
            <button
              className="add-workout-btn"
              onClick={() => onAddWorkout(dayObj.date, timeSlot)}
              title={`Add workout to ${timeSlot}`}
              aria-label={`Add workout to ${timeSlot}`}
            >
              +
            </button>
          )}
        </div>
        {shouldShowWeather && (
          <div className="time-slot-weather-mini">
            <span className="weather-emoji-mini">{weatherInfo.emoji}</span>
            <span className="weather-temp-mini">
              {Math.round(weather.actual_temperature)} | {Math.round(weather.apparent_temperature)}
            </span>
          </div>
        )}
        {shouldShowWeatherError && (
          <div className="time-slot-weather-unavailable">Weather unavailable</div>
        )}
      </div>

      {/* Full weather details when not loading or on error */}
      {shouldShowWeather && !loading && !error && (
        <div className="time-slot-weather-details">
          <div className="weather-detail">
            <span className="label">Rain:</span>
            <span className="value">{weather.rain_probability}%</span>
          </div>
          <div className="weather-detail">
            <span className="label">Wind:</span>
            <span className="value">{Math.round(weather.windspeed)} mph</span>
          </div>
        </div>
      )}

      {/* Tri-club events — disabled until tri club resumes in September.
          To re-enable: remove the comment wrapper and restore the hasContent line above. */}
      {/* triClubEvents.length > 0 && (
        <div className="tri-club-events">
          {triClubEvents.map((event, idx) => (
            <div key={idx} className="tri-club-event">
              {event.formattedTime} tri club {event.activity.toLowerCase()}
            </div>
          ))}
        </div>
      ) */}

      {/* Workouts */}
      <div className="time-slot-workouts">
        {workouts.map((workout, idx) => (
          <WorkoutBadge
            key={idx}
            workout={workout}
            onDragStart={onWorkoutDragStart}
            onDragEnd={onWorkoutDragEnd}
            onWorkoutClick={() => onWorkoutClick(workout)}
            onSelectionToggle={onWorkoutSelectionToggle}
            onHeatTrainingToggle={onWorkoutHeatTrainingChange}
            draggedWorkoutId={draggedWorkout?.id}
          />
        ))}
      </div>

      {/* Placeholder for empty unscheduled slots */}
      {draggedWorkout && !hasContent && (
        <div className="time-slot-placeholder">
          {getTimeOfDayLabel(timeSlot)}
        </div>
      )}
    </div>
  );
}

DayTimeSlot.propTypes = {
  dayObj: PropTypes.shape({
    date: PropTypes.object.isRequired,
  }).isRequired,
  timeSlot: PropTypes.string.isRequired,
  workouts: PropTypes.array,
  triClubEvents: PropTypes.array,
  draggedWorkout: PropTypes.object,
  dragOverDate: PropTypes.string,
  dragOverTimeSlot: PropTypes.string,
  onDragOver: PropTypes.func.isRequired,
  onDragLeave: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
  onWorkoutDragStart: PropTypes.func.isRequired,
  onWorkoutDragEnd: PropTypes.func.isRequired,
  onWorkoutClick: PropTypes.func.isRequired,
  onWorkoutSelectionToggle: PropTypes.func,
  onWorkoutHeatTrainingChange: PropTypes.func,
  onAddWorkout: PropTypes.func,
  getTimeOfDayLabel: PropTypes.func.isRequired,
};

DayTimeSlot.defaultProps = {
  workouts: [],
  triClubEvents: [],
  draggedWorkout: null,
  dragOverDate: null,
  dragOverTimeSlot: null,
  onWorkoutHeatTrainingChange: null,
  onAddWorkout: null,
};

export default DayTimeSlot;
